import { FC, useMemo } from 'react';

import { Box } from '@bonusly/design-system';

import { BizyRenderer } from './bizy-renderer';
import { BizyProps, BizySize, resolveBizyState } from './types';

/**
 * Bizy - The Bonusly bee mascot component
 *
 * A cute bee character that can display different emotional states.
 * Supports smooth part-based animations between states (antennae, wings, body, eyes, arms).
 * The bored state uses crossfade transition due to its unique rotated pose.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Bizy state="excited" />
 *
 * // With custom size
 * <Bizy state="thinkingLeft" size="lg" />
 *
 * // Numeric size
 * <Bizy state="curious" size={100} />
 * ```
 */
export const Bizy: FC<BizyProps> = ({
	state = 'curious',
	size = 'md',
	freeze = false,
	className,
	animations,
	armStrokeColor,
	armStrokeOpacity,
	skipEntranceFade = false,
	texture,
	onBodyClick,
}) => {
	const sizeValue = useMemo(() => getSizeValue(size), [size]);
	const shouldRenderTexture = texture ?? sizeValue >= 48;

	return (
		<Box className={className} display="inline-flex" alignItems="center" justifyContent="center" color="bizy.outline">
			<BizyRenderer
				state={resolveBizyState(state)}
				size={sizeValue}
				freeze={freeze}
				animations={animations}
				armStrokeColor={armStrokeColor}
				armStrokeOpacity={armStrokeOpacity}
				skipEntranceFade={skipEntranceFade}
				texture={shouldRenderTexture}
				onBodyClick={onBodyClick}
			/>
		</Box>
	);
};

const getSizeValue = (size: BizySize): number => {
	if (typeof size === 'number') return size;
	const sizeMap: Record<string, number> = { sm: 48, md: 80, lg: 120, xl: 200 };
	return sizeMap[size] || 80;
};
